import { useEffect, useState } from "react";
import { AlertTriangle, Eye, Pencil, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { api, fmtBR, fmtWhatsApp, formatApiError, WEEKDAY_NAMES } from "@/lib/api";
import { calcAge } from "@/pages/Students";

const LEAD_STATUS = {
  novo: "Novo",
  em_analise: "Em análise",
  aguardando_professor: "Aguardando professor",
  horario_aceito: "Horário aceito",
  horario_alternativo_sugerido: "Horário alternativo sugerido",
  contato_realizado: "Contato realizado",
  convertido: "Convertido em matrícula",
  perdido: "Perdido",
  desistiu: "Desistiu",
};

const HISTORY_LABELS = {
  criacao: "Criação", alteracao: "Alteração", mudanca_status: "Mudança de status",
  alteracao_curso: "Alteração de curso", alteracao_horario: "Alteração de horário",
  conversao: "Conversão em matrícula", perda: "Perda", desistencia: "Desistência",
  novo_contato_publico: "Novo contato pelo site", atualizado_via_whatsapp: "Atualização via WhatsApp",
  contato_registrado: "Contato registrado",
};

const CONTACT_RESULT_LABELS = {
  conversou: "Conversou",
  sem_retorno: "Sem retorno",
  reagendou: "Reagendou contato",
  sem_interesse: "Sem interesse",
};

const STATUS_VARIANT = {
  novo: "default", em_analise: "secondary", aguardando_professor: "secondary",
  horario_aceito: "default", horario_alternativo_sugerido: "secondary",
  contato_realizado: "secondary", convertido: "default", perdido: "destructive", desistiu: "destructive",
};

const EMPTY = {
  full_name: "", birth_date: "", whatsapp: "", email: "", city: "",
  course_id: "", modality_id: "", requested_schedule: "", suggested_schedule: "",
  origin: "Site", admin_notes: "", status: "novo", next_contact_at: "",
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [lookups, setLookups] = useState({ courses: [], modalities: [], teachers: [], origins: [] });
  const [filters, setFilters] = useState({ search: "", status: "todos", course_id: "todos", origin: "todas", period_start: "", period_end: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [duplicates, setDuplicates] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailRequests, setDetailRequests] = useState([]);
  const [forwardForm, setForwardForm] = useState({ teacher_id: "", weekday: "", start_time: "", admin_note: "" });
  const [forwardError, setForwardError] = useState("");
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertCtx, setConvertCtx] = useState(null);
  const [convertForm, setConvertForm] = useState(null);
  const [convertError, setConvertError] = useState("");
  const [convertMatches, setConvertMatches] = useState(null);
  const [converting, setConverting] = useState(false);
  const [contactForm, setContactForm] = useState({ result: "", note: "", next_contact_at: "" });
  const [contactError, setContactError] = useState("");
  const [contactSaving, setContactSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, pages: 1 });
  const PAGE_SIZE = 25;

  const REQUEST_STATUS_LABELS = {
    aguardando_professor: "Aguardando professor",
    aceito: "Aceito pelo professor",
    alternativo_sugerido: "Horário alternativo sugerido",
    sem_disponibilidade: "Professor sem disponibilidade",
    cancelado: "Cancelado",
    expirado: "Expirado",
  };

  const load = async () => {
    const params = { page, page_size: PAGE_SIZE };
    if (filters.search.trim()) params.search = filters.search.trim();
    if (filters.status !== "todos") params.status = filters.status;
    if (filters.course_id !== "todos") params.course_id = filters.course_id;
    if (filters.origin !== "todas") params.origin = filters.origin;
    if (filters.period_start) params.period_start = filters.period_start;
    if (filters.period_end) params.period_end = filters.period_end;
    const [l, c, m, t, s] = await Promise.all([
      api.get("/leads", { params }),
      api.get("/courses"),
      api.get("/modalities"),
      api.get("/teachers"),
      api.get("/settings"),
    ]);
    setLeads(l.data.items);
    setMeta({ total: l.data.total, pages: Math.max(l.data.pages, 1) });
    setLookups({
      courses: c.data.filter((x) => x.active),
      modalities: m.data.filter((x) => x.active),
      teachers: t.data,
      origins: s.data.lead_origins || [],
    });
  };

  // mudança de filtro/busca → volta para a primeira página
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.status, filters.course_id, filters.origin, filters.period_start, filters.period_end]);

  useEffect(() => {
    load().catch((e) => toast.error(formatApiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters.status, filters.course_id, filters.origin, filters.period_start, filters.period_end]);

  const openDialog = (lead) => {
    setEditing(lead || null);
    setForm(
      lead
        ? {
            full_name: lead.full_name, birth_date: lead.birth_date, whatsapp: lead.whatsapp,
            email: lead.email || "", city: lead.city || "", course_id: lead.course_id,
            modality_id: lead.modality_id, requested_schedule: lead.requested_schedule || "",
            suggested_schedule: lead.suggested_schedule || "", origin: lead.origin,
            admin_notes: lead.admin_notes || "", status: lead.status,
            next_contact_at: lead.next_contact_at ? String(lead.next_contact_at).slice(0, 16) : "",
          }
        : EMPTY
    );
    setError("");
    setDuplicates(null);
    setDialogOpen(true);
  };

  const save = async (allowDuplicate = false) => {
    setSaving(true);
    setError("");
    setDuplicates(null);
    try {
      const payload = { ...form };
      ["email", "city", "requested_schedule", "suggested_schedule", "admin_notes"].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      payload.next_contact_at = form.next_contact_at || "";
      if (allowDuplicate) payload.allow_duplicate = true;
      if (editing) await api.put(`/leads/${editing.id}`, payload);
      else await api.post("/leads", payload);
      toast.success(editing ? "Interessado atualizado." : "Interessado cadastrado.");
      setDialogOpen(false);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.duplicates) {
        setDuplicates(detail);
      } else {
        setError(formatApiError(err));
      }
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (lead) => {
    try {
      const [{ data }, { data: reqs }] = await Promise.all([
        api.get(`/leads/${lead.id}`),
        api.get("/requests", { params: { lead_id: lead.id } }),
      ]);
      setDetail(data);
      setDetailRequests(reqs);
      setForwardForm({ teacher_id: "", weekday: "", start_time: "", admin_note: "" });
      setForwardError("");
      setContactForm({ result: "", note: "", next_contact_at: "" });
      setContactError("");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const forward = async () => {
    setForwardError("");
    try {
      await api.post(`/leads/${detail.id}/forward`, {
        teacher_id: forwardForm.teacher_id,
        weekday: Number(forwardForm.weekday),
        start_time: forwardForm.start_time,
        admin_note: forwardForm.admin_note || undefined,
      });
      toast.success("Interessado encaminhado ao professor.");
      openDetail(detail);
      load();
    } catch (err) {
      setForwardError(formatApiError(err));
    }
  };

  const saveContact = async () => {
    setContactSaving(true);
    setContactError("");
    try {
      const payload = { result: contactForm.result, note: contactForm.note.trim() };
      if (contactForm.next_contact_at) payload.next_contact_at = contactForm.next_contact_at;
      await api.post(`/leads/${detail.id}/contacts`, payload);
      toast.success("Contato registrado.");
      setContactForm({ result: "", note: "", next_contact_at: "" });
      openDetail(detail);
      load();
    } catch (err) {
      setContactError(formatApiError(err));
    } finally {
      setContactSaving(false);
    }
  };

  const openConvert = async () => {
    setConvertError("");
    setConvertMatches(null);
    try {
      const { data } = await api.get(`/leads/${detail.id}/conversion-context`);
      setConvertCtx(data);
      setConvertForm({
        room_id: "",
        start_date: "",
        teacher_id: data.request.teacher_id,
        modality_id: data.request.modality_id,
        weekday: String(data.request.weekday),
        start_time: data.request.start_time,
        student_mode: data.matched_students.length > 0 ? "existing" : "new",
        student_id: data.matched_students[0]?.id || "",
        guardian_mode: "existing",
        guardian_id: "",
        guardian_name: "",
        guardian_whatsapp: "",
        guardian_email: "",
        guardian_relationship: "responsavel",
        confirm_new_student: false,
      });
      setConvertOpen(true);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submitConvert = async (overrides = {}) => {
    setConverting(true);
    setConvertError("");
    setConvertMatches(null);
    try {
      const f = { ...convertForm, ...overrides };
      const payload = {
        room_id: f.room_id,
        start_date: f.start_date,
        teacher_id: f.teacher_id,
        modality_id: f.modality_id,
        weekday: Number(f.weekday),
        start_time: f.start_time,
      };
      if (f.student_mode === "existing" && f.student_id) payload.student_id = f.student_id;
      if (f.confirm_new_student) payload.confirm_new_student = true;
      if (convertCtx.is_minor) {
        if (f.guardian_mode === "existing" && f.guardian_id) payload.guardian_id = f.guardian_id;
        if (f.guardian_mode === "new") {
          payload.guardian_name = f.guardian_name;
          payload.guardian_whatsapp = f.guardian_whatsapp;
          payload.guardian_email = f.guardian_email || undefined;
          payload.guardian_relationship = f.guardian_relationship;
        }
      }
      const { data } = await api.post(`/leads/${detail.id}/convert`, payload);
      toast.success(`Matrícula criada! ${data.lessons_generated} aula(s) gerada(s).`);
      setConvertOpen(false);
      openDetail(detail);
      load();
    } catch (err) {
      const det = err?.response?.data?.detail;
      if (err?.response?.status === 409 && det?.matches) {
        setConvertMatches(det);
      } else {
        setConvertError(formatApiError(err));
      }
    } finally {
      setConverting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold">Interessados</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Pessoas que demonstraram interesse no IMJ e ainda não são alunos matriculados.
          </p>
        </div>
        <Button onClick={() => openDialog(null)} className="bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="lead-add-button">
          <Plus size={16} className="mr-2" /> Novo interessado
        </Button>
      </div>

      <div className="space-y-3">
        <form onSubmit={(e) => { e.preventDefault(); if (page === 1) load(); else setPage(1); }} className="flex items-center gap-2">
          <Input
            placeholder="Pesquisar por nome ou WhatsApp..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            data-testid="lead-search-input"
          />
          <Button type="submit" variant="outline" size="icon" data-testid="lead-search-button">
            <Search size={16} />
          </Button>
        </form>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger data-testid="lead-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os status</SelectItem>
              {Object.entries(LEAD_STATUS).map(([v, l]) => (
                <SelectItem key={v} value={v}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.course_id} onValueChange={(v) => setFilters({ ...filters, course_id: v })}>
            <SelectTrigger data-testid="lead-filter-course"><SelectValue placeholder="Curso" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os cursos</SelectItem>
              {lookups.courses.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.origin} onValueChange={(v) => setFilters({ ...filters, origin: v })}>
            <SelectTrigger data-testid="lead-filter-origin"><SelectValue placeholder="Origem" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas as origens</SelectItem>
              {lookups.origins.map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input type="date" value={filters.period_start} onChange={(e) => setFilters({ ...filters, period_start: e.target.value })} data-testid="lead-filter-period-start" title="Criado a partir de" />
          <Input type="date" value={filters.period_end} onChange={(e) => setFilters({ ...filters, period_end: e.target.value })} data-testid="lead-filter-period-end" title="Criado até" />
        </div>
      </div>

      <div className="overflow-x-auto border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-wider">Nome</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Idade</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Curso</TableHead>
              <TableHead className="hidden text-xs uppercase tracking-wider md:table-cell">Modalidade</TableHead>
              <TableHead className="hidden text-xs uppercase tracking-wider lg:table-cell">Horário</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
              <TableHead className="hidden text-xs uppercase tracking-wider md:table-cell">Origem</TableHead>
              <TableHead className="hidden text-xs uppercase tracking-wider lg:table-cell">Data</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-wider">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {leads.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="p-6 text-sm text-muted-foreground" data-testid="leads-empty">
                  Nenhum interessado encontrado.
                </TableCell>
              </TableRow>
            )}
            {leads.map((lead) => (
              <TableRow key={lead.id} className="transition-colors hover:bg-zinc-50" data-testid={`lead-row-${lead.id}`}>
                <TableCell className="font-medium">
                  {lead.full_name}
                  {lead.needs_school_analysis && (
                    <Badge variant="secondary" className="ml-2 border border-primary text-[10px] text-primary" data-testid={`lead-minor-badge-${lead.id}`}>
                      Análise da escola
                    </Badge>
                  )}
                </TableCell>
                <TableCell>{lead.age}</TableCell>
                <TableCell>{lead.course_name}</TableCell>
                <TableCell className="hidden md:table-cell">{lead.modality_duration ? `${lead.modality_duration} min` : "—"}</TableCell>
                <TableCell className="hidden lg:table-cell">{lead.requested_schedule || lead.suggested_schedule || "—"}</TableCell>
                <TableCell>
                  <Badge
                    variant={STATUS_VARIANT[lead.status] || "secondary"}
                    className={lead.status === "novo" || lead.status === "horario_aceito" || lead.status === "convertido" ? "bg-primary text-primary-foreground" : ""}
                  >
                    {LEAD_STATUS[lead.status] || lead.status}
                  </Badge>
                </TableCell>
                <TableCell className="hidden md:table-cell">{lead.origin}</TableCell>
                <TableCell className="hidden lg:table-cell">{fmtBR((lead.created_at || "").slice(0, 10))}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" title="Detalhes" onClick={() => openDetail(lead)} data-testid={`lead-view-${lead.id}`}>
                    <Eye size={16} />
                  </Button>
                  <Button variant="ghost" size="icon" title="Editar" onClick={() => openDialog(lead)} data-testid={`lead-edit-${lead.id}`}>
                    <Pencil size={16} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="flex flex-wrap items-center justify-between gap-2" data-testid="lead-pagination">
          <p className="text-sm text-muted-foreground" data-testid="lead-page-info">
            {meta.total} interessados · Página {page} de {meta.pages}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="lead-prev-page">
              Anterior
            </Button>
            <Button variant="outline" size="sm" disabled={page >= meta.pages} onClick={() => setPage(page + 1)} data-testid="lead-next-page">
              Próxima
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="lead-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar interessado" : "Novo interessado"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); save(false); }} className="space-y-4">
            {error && <p className="border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive" data-testid="lead-form-error">{error}</p>}
            {duplicates && (
              <div className="border border-primary/60 bg-primary/10 p-3" data-testid="lead-duplicates-alert">
                <p className="flex items-center gap-2 text-sm font-semibold">
                  <AlertTriangle size={16} className="text-primary" /> {duplicates.message}
                </p>
                <ul className="mt-2 list-inside list-disc text-sm">
                  {duplicates.duplicates.map((d) => (
                    <li key={d.id}>{d.full_name} — {LEAD_STATUS[d.status] || d.status} ({fmtWhatsApp(d.whatsapp)})</li>
                  ))}
                </ul>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  disabled={saving}
                  onClick={() => save(true)}
                  data-testid="lead-create-anyway-button"
                >
                  Criar mesmo assim
                </Button>
              </div>
            )}
            <div className="space-y-2">
              <Label>Nome completo *</Label>
              <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="lead-name-input" />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Data de nascimento *</Label>
                <Input type="date" required value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} data-testid="lead-birthdate-input" />
                {form.birth_date && (
                  <p className="text-xs text-muted-foreground" data-testid="lead-computed-age">
                    Idade: {calcAge(form.birth_date)} anos
                    {calcAge(form.birth_date) < 7 && (
                      <span className="ml-1 font-semibold text-primary" data-testid="lead-minor-warning">— Necessita análise da escola</span>
                    )}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Cidade</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Cabreúva" data-testid="lead-city-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>WhatsApp *</Label>
                <Input required value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="(11) 99999-9999" data-testid="lead-whatsapp-input" />
              </div>
              <div className="space-y-2">
                <Label>E-mail</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="lead-email-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Curso desejado *</Label>
                <Select value={form.course_id} onValueChange={(v) => setForm({ ...form, course_id: v })}>
                  <SelectTrigger data-testid="lead-course-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {lookups.courses.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Modalidade *</Label>
                <Select value={form.modality_id} onValueChange={(v) => setForm({ ...form, modality_id: v })}>
                  <SelectTrigger data-testid="lead-modality-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {lookups.modalities.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.duration_minutes} min — R$ {m.monthly_price.toFixed(2)}/mês</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Horário solicitado</Label>
                <Input value={form.requested_schedule} onChange={(e) => setForm({ ...form, requested_schedule: e.target.value })} placeholder="Ex.: Segunda 18h" data-testid="lead-requested-schedule-input" />
              </div>
              <div className="space-y-2">
                <Label>Horário sugerido pelo interessado</Label>
                <Input value={form.suggested_schedule} onChange={(e) => setForm({ ...form, suggested_schedule: e.target.value })} placeholder="Ex.: Sábado de manhã" data-testid="lead-suggested-schedule-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Próximo contato (data e hora)</Label>
              <Input type="datetime-local" value={form.next_contact_at} onChange={(e) => setForm({ ...form, next_contact_at: e.target.value })} data-testid="lead-next-contact-input" />
            </div>
            <div className="space-y-2">
              <Label>Origem *</Label>
              <Select value={form.origin} onValueChange={(v) => setForm({ ...form, origin: v })}>
                <SelectTrigger data-testid="lead-origin-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {lookups.origins.map((o) => (
                    <SelectItem key={o} value={o}>{o}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {editing && (
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(LEAD_STATUS).map(([v, l]) => (
                      <SelectItem key={v} value={v}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Observações administrativas</Label>
              <Textarea rows={2} value={form.admin_notes} onChange={(e) => setForm({ ...form, admin_notes: e.target.value })} data-testid="lead-notes-input" />
            </div>
            <Button
              type="submit"
              disabled={saving || !form.full_name || !form.birth_date || !form.whatsapp || !form.course_id || !form.modality_id}
              className="w-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90"
              data-testid="lead-save-button"
            >
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="lead-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detail?.full_name}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-6">
              {detail.needs_school_analysis && (
                <p className="border border-primary/60 bg-primary/10 p-3 text-sm font-medium" data-testid="lead-detail-minor-warning">
                  Interessado com menos de 7 anos — necessita análise da escola antes de qualquer confirmação.
                </p>
              )}
              <div>
                <h3 className="font-heading text-base font-semibold">Dados do interessado</h3>
                <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
                  <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Nascimento</p><p className="font-medium">{fmtBR(detail.birth_date)}</p></div>
                  <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Idade</p><p className="font-medium" data-testid="lead-detail-age">{detail.age} anos</p></div>
                  <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Cidade</p><p className="font-medium">{detail.city || "—"}</p></div>
                  <div><p className="text-xs uppercase tracking-wider text-muted-foreground">WhatsApp</p><p className="font-medium">{fmtWhatsApp(detail.whatsapp)}</p></div>
                  <div className="col-span-2"><p className="text-xs uppercase tracking-wider text-muted-foreground">E-mail</p><p className="font-medium">{detail.email || "—"}</p></div>
                </div>
              </div>
              <div>
                <h3 className="font-heading text-base font-semibold">Interesse</h3>
                <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
                  <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Curso</p><p className="font-medium">{detail.course_name}</p></div>
                  <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Modalidade</p><p className="font-medium">{detail.modality_name}</p></div>
                  <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Origem</p><p className="font-medium">{detail.origin}</p></div>
                  <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Horário solicitado</p><p className="font-medium">{detail.requested_schedule || "—"}</p></div>
                  <div className="col-span-2"><p className="text-xs uppercase tracking-wider text-muted-foreground">Horário sugerido</p><p className="font-medium">{detail.suggested_schedule || "—"}</p></div>
                </div>
              </div>
              <div>
                <h3 className="font-heading text-base font-semibold">Status</h3>
                <div className="mt-2">
                  <Badge variant={STATUS_VARIANT[detail.status] || "secondary"} className={["novo", "horario_aceito", "convertido"].includes(detail.status) ? "bg-primary text-primary-foreground" : ""} data-testid="lead-detail-status">
                    {LEAD_STATUS[detail.status] || detail.status}
                  </Badge>
                </div>
                <div className="mt-3 space-y-2" data-testid="lead-history-list">
                  {(detail.history || []).map((h) => (
                    <div key={h.id} className="border border-border p-2 text-xs">
                      <span className="font-semibold">{HISTORY_LABELS[h.action] || h.action}</span>
                      <span className="text-muted-foreground"> · {h.user_name} · {new Date(h.created_at).toLocaleString("pt-BR")}</span>
                      {h.changes?.status && (
                        <p className="mt-1 text-muted-foreground">
                          {LEAD_STATUS[h.changes.status.de] || h.changes.status.de} → {LEAD_STATUS[h.changes.status.para] || h.changes.status.para}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-heading text-base font-semibold">Contatos</h3>
                {detail.next_contact_at && (
                  <p className={`mt-2 border p-2 text-sm font-medium ${new Date(detail.next_contact_at) < new Date() ? "border-destructive/50 bg-destructive/10 text-destructive" : "border-border bg-zinc-50"}`} data-testid="lead-next-contact-info">
                    Próximo contato: {new Date(detail.next_contact_at).toLocaleString("pt-BR")}
                    {new Date(detail.next_contact_at) < new Date() ? " — vencido" : ""}
                  </p>
                )}
                <div className="mt-2 space-y-2" data-testid="lead-contacts-list">
                  {(detail.contacts || []).length === 0 && (
                    <p className="text-sm text-muted-foreground" data-testid="lead-contacts-empty">Nenhum contato registrado.</p>
                  )}
                  {(detail.contacts || []).map((c) => (
                    <div key={c.id} className="border border-border p-2 text-xs" data-testid={`lead-contact-${c.id}`}>
                      <span className="font-semibold">{CONTACT_RESULT_LABELS[c.result] || c.result}</span>
                      <span className="text-muted-foreground"> · {c.user_name} · {new Date(c.contacted_at).toLocaleString("pt-BR")}</span>
                      <p className="mt-1 text-muted-foreground">{c.note}</p>
                    </div>
                  ))}
                </div>
                {!["convertido", "perdido", "desistiu"].includes(detail.status) && (
                  <div className="mt-3 space-y-3 border border-border bg-zinc-50 p-3" data-testid="contact-form-section">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Registrar contato</p>
                    {contactError && (
                      <p className="border border-destructive/50 bg-destructive/10 p-2 text-sm text-destructive" data-testid="contact-form-error">{contactError}</p>
                    )}
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <Select value={contactForm.result} onValueChange={(v) => setContactForm({ ...contactForm, result: v })}>
                        <SelectTrigger data-testid="contact-result-select"><SelectValue placeholder="Resultado" /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(CONTACT_RESULT_LABELS).map(([v, l]) => (
                            <SelectItem key={v} value={v}>{l}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input type="datetime-local" value={contactForm.next_contact_at} onChange={(e) => setContactForm({ ...contactForm, next_contact_at: e.target.value })} data-testid="contact-next-input" />
                    </div>
                    <Textarea rows={2} placeholder="Observação do contato" value={contactForm.note} onChange={(e) => setContactForm({ ...contactForm, note: e.target.value })} data-testid="contact-note-input" />
                    <Button type="button" size="sm" disabled={contactSaving || !contactForm.result || !contactForm.note.trim()} onClick={saveContact} className="bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="contact-save-button">
                      {contactSaving ? "Salvando..." : "Registrar contato"}
                    </Button>
                  </div>
                )}
              </div>
              <div>
                <h3 className="font-heading text-base font-semibold">Solicitações a professores</h3>
                <div className="mt-2 space-y-2" data-testid="lead-requests-list">
                  {detailRequests.length === 0 && (
                    <p className="text-sm text-muted-foreground" data-testid="lead-requests-empty">Nenhum encaminhamento realizado.</p>
                  )}
                  {detailRequests.map((r) => (
                    <div key={r.id} className="flex flex-wrap items-center gap-2 border border-border p-3 text-sm" data-testid={`lead-request-${r.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium">{r.teacher_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {WEEKDAY_NAMES[r.weekday]} {r.start_time}–{r.end_time} · {fmtBR((r.forwarded_at || "").slice(0, 10))}
                        </p>
                        {r.suggested_schedule && (
                          <p className="mt-1 text-xs font-medium text-primary">Sugerido: {r.suggested_schedule.label}</p>
                        )}
                      </div>
                      <Badge variant={r.status === "aceito" ? "default" : ["sem_disponibilidade", "cancelado", "expirado"].includes(r.status) ? "destructive" : "secondary"} className={r.status === "aceito" ? "bg-primary text-primary-foreground" : ""}>
                        {REQUEST_STATUS_LABELS[r.status] || r.status}
                      </Badge>
                    </div>
                  ))}
                </div>

                {detail.status === "horario_aceito" && detailRequests.some((r) => r.status === "aceito") && (
                  <Button
                    className="mt-3 w-full bg-primary font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                    onClick={openConvert}
                    data-testid="lead-convert-button"
                  >
                    Converter em matrícula
                  </Button>
                )}
                {detail.status === "convertido" && (
                  <p className="mt-3 border border-primary/60 bg-primary/10 p-3 text-sm font-medium" data-testid="lead-converted-info">
                    Matrícula criada — interessado convertido em aluno. A matrícula pode ser visualizada na página Matrículas.
                  </p>
                )}

                {!["convertido", "perdido", "desistiu"].includes(detail.status) &&
                  !detailRequests.some((r) => r.status === "aguardando_professor") && (
                  <div className="mt-4 space-y-3 border border-border bg-zinc-50 p-3" data-testid="forward-section">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Encaminhar ao professor</p>
                    {forwardError && (
                      <p className="border border-destructive/50 bg-destructive/10 p-2 text-sm text-destructive" data-testid="forward-error">{forwardError}</p>
                    )}
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                      <Select value={forwardForm.teacher_id} onValueChange={(v) => setForwardForm({ ...forwardForm, teacher_id: v })}>
                        <SelectTrigger data-testid="forward-teacher-select"><SelectValue placeholder="Professor" /></SelectTrigger>
                        <SelectContent>
                          {lookups.teachers
                            .filter((t) => t.course_ids.includes(detail.course_id))
                            .map((t) => (
                              <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <Select value={forwardForm.weekday} onValueChange={(v) => setForwardForm({ ...forwardForm, weekday: v })}>
                        <SelectTrigger data-testid="forward-weekday-select"><SelectValue placeholder="Dia" /></SelectTrigger>
                        <SelectContent>
                          {WEEKDAY_NAMES.map((n, i) => (
                            <SelectItem key={i} value={String(i)}>{n}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input type="time" value={forwardForm.start_time} onChange={(e) => setForwardForm({ ...forwardForm, start_time: e.target.value })} data-testid="forward-time-input" />
                    </div>
                    <Input value={forwardForm.admin_note} onChange={(e) => setForwardForm({ ...forwardForm, admin_note: e.target.value })} placeholder="Observação interna (opcional)" data-testid="forward-note-input" />
                    <Button
                      size="sm"
                      disabled={!forwardForm.teacher_id || forwardForm.weekday === "" || !forwardForm.start_time}
                      onClick={forward}
                      className="bg-primary text-primary-foreground transition-colors hover:bg-primary/90"
                      data-testid="forward-submit-button"
                    >
                      Encaminhar
                    </Button>
                  </div>
                )}
              </div>

              {detail.admin_notes && (
                <div>
                  <h3 className="font-heading text-base font-semibold">Observações administrativas</h3>
                  <p className="mt-2 text-sm">{detail.admin_notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="convert-dialog">
          <DialogHeader>
            <DialogTitle>Converter em matrícula — {detail?.full_name}</DialogTitle>
          </DialogHeader>
          {convertCtx && convertForm && (
            <div className="space-y-5">
              {convertError && (
                <p className="border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive" data-testid="convert-error">{convertError}</p>
              )}
              {convertMatches && (
                <div className="border border-primary/60 bg-primary/10 p-3" data-testid="convert-matches-alert">
                  <p className="text-sm font-semibold">{convertMatches.message}</p>
                  <ul className="mt-2 list-inside list-disc text-sm">
                    {convertMatches.matches.map((m) => (
                      <li key={m.id}>{m.full_name} — {m.age} anos ({m.whatsapp})</li>
                    ))}
                  </ul>
                  <div className="mt-3 flex gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={() => { setConvertForm({ ...convertForm, student_mode: "existing", student_id: convertMatches.matches[0].id }); setConvertMatches(null); }} data-testid="convert-use-existing-button">
                      Usar aluno existente
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => submitConvert({ student_mode: "new", confirm_new_student: true })} data-testid="convert-confirm-new-button">
                      Criar novo aluno mesmo assim
                    </Button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-x-6 gap-y-3 border border-border bg-zinc-50 p-3 text-sm sm:grid-cols-3">
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Aluno</p><p className="font-medium">{convertCtx.lead.full_name}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Nascimento</p><p className="font-medium">{fmtBR(convertCtx.lead.birth_date)}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Idade</p><p className="font-medium" data-testid="convert-age">{convertCtx.lead.age} anos</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Curso</p><p className="font-medium">{convertCtx.course?.name}</p></div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Professor</Label>
                  <Select value={convertForm.teacher_id} onValueChange={(v) => setConvertForm({ ...convertForm, teacher_id: v })}>
                    <SelectTrigger data-testid="convert-teacher-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {convertCtx.compatible_teachers.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Modalidade</Label>
                  <Select value={convertForm.modality_id} onValueChange={(v) => setConvertForm({ ...convertForm, modality_id: v })}>
                    <SelectTrigger data-testid="convert-modality-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {convertCtx.modalities.map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.name} — {m.duration_minutes} min</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label>Dia da semana</Label>
                  <Select value={convertForm.weekday} onValueChange={(v) => setConvertForm({ ...convertForm, weekday: v })}>
                    <SelectTrigger data-testid="convert-weekday-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {WEEKDAY_NAMES.map((n, i) => (
                        <SelectItem key={i} value={String(i)}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Horário</Label>
                  <Input type="time" required value={convertForm.start_time} onChange={(e) => setConvertForm({ ...convertForm, start_time: e.target.value })} data-testid="convert-time-input" />
                </div>
                <div className="space-y-2">
                  <Label>Sala *</Label>
                  <Select value={convertForm.room_id} onValueChange={(v) => setConvertForm({ ...convertForm, room_id: v })}>
                    <SelectTrigger data-testid="convert-room-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      {convertCtx.rooms.map((r) => (
                        <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Data de início *</Label>
                <Input type="date" required value={convertForm.start_date} onChange={(e) => setConvertForm({ ...convertForm, start_date: e.target.value })} data-testid="convert-start-date-input" />
              </div>

              {convertCtx.matched_students.length > 0 && (
                <div className="space-y-2">
                  <Label>Aluno</Label>
                  <Select value={convertForm.student_mode} onValueChange={(v) => setConvertForm({ ...convertForm, student_mode: v })}>
                    <SelectTrigger data-testid="convert-student-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">Criar novo aluno</SelectItem>
                      <SelectItem value="existing">Reutilizar aluno existente</SelectItem>
                    </SelectContent>
                  </Select>
                  {convertForm.student_mode === "existing" && (
                    <Select value={convertForm.student_id} onValueChange={(v) => setConvertForm({ ...convertForm, student_id: v })}>
                      <SelectTrigger data-testid="convert-student-select"><SelectValue placeholder="Selecione o aluno" /></SelectTrigger>
                      <SelectContent>
                        {convertCtx.matched_students.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.full_name} ({s.age} anos)</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {convertCtx.is_minor && (
                <div className="space-y-3 border border-border p-3" data-testid="convert-guardian-section">
                  <p className="text-sm font-semibold">Responsável (obrigatório — menor de idade)</p>
                  <Select value={convertForm.guardian_mode} onValueChange={(v) => setConvertForm({ ...convertForm, guardian_mode: v })}>
                    <SelectTrigger data-testid="convert-guardian-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="existing">Selecionar responsável existente</SelectItem>
                      <SelectItem value="new">Cadastrar novo responsável</SelectItem>
                    </SelectContent>
                  </Select>
                  {convertForm.guardian_mode === "existing" ? (
                    <Select value={convertForm.guardian_id} onValueChange={(v) => setConvertForm({ ...convertForm, guardian_id: v })}>
                      <SelectTrigger data-testid="convert-guardian-select"><SelectValue placeholder="Selecione o responsável" /></SelectTrigger>
                      <SelectContent>
                        {convertCtx.guardians.map((g) => (
                          <SelectItem key={g.id} value={g.id}>{g.full_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Input placeholder="Nome completo do responsável *" value={convertForm.guardian_name} onChange={(e) => setConvertForm({ ...convertForm, guardian_name: e.target.value })} data-testid="convert-guardian-name-input" />
                        <Input placeholder="WhatsApp *" value={convertForm.guardian_whatsapp} onChange={(e) => setConvertForm({ ...convertForm, guardian_whatsapp: e.target.value })} data-testid="convert-guardian-whatsapp-input" />
                      </div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Input type="email" placeholder="E-mail *" value={convertForm.guardian_email} onChange={(e) => setConvertForm({ ...convertForm, guardian_email: e.target.value })} data-testid="convert-guardian-email-input" />
                        <Select value={convertForm.guardian_relationship} onValueChange={(v) => setConvertForm({ ...convertForm, guardian_relationship: v })}>
                          <SelectTrigger data-testid="convert-guardian-relationship-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="responsavel">Responsável</SelectItem>
                            <SelectItem value="pai">Pai</SelectItem>
                            <SelectItem value="mae">Mãe</SelectItem>
                            <SelectItem value="avo">Avô/Avó</SelectItem>
                            <SelectItem value="tutor">Tutor</SelectItem>
                            <SelectItem value="outro">Outro</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <Button
                onClick={() => submitConvert()}
                disabled={converting || !convertForm.room_id || !convertForm.start_date || (convertCtx.is_minor && convertForm.guardian_mode === "existing" && !convertForm.guardian_id) || (convertCtx.is_minor && convertForm.guardian_mode === "new" && (!convertForm.guardian_name || !convertForm.guardian_whatsapp || !convertForm.guardian_email))}
                className="w-full bg-primary font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                data-testid="convert-submit-button"
              >
                {converting ? "Convertendo..." : "Confirmar conversão e criar matrícula"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
